import { useRef, useState, useEffect } from 'react';
import { Bot, Upload, Loader2, CheckCircle2, AlertTriangle, Sparkles, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { SupplierCombobox } from './SupplierCombobox';

const PROCESSING_LINES = [
  'Checking the uploaded format…',
  'Bringing it to the right credential shape…',
  'Matching each account to a collector…',
  'Nearly there…',
];

const ISSUE_LABEL = {
  missing_username: 'username',
  missing_password: 'password',
  no_supplier_match: 'supplier',
};

/**
 * AI-assisted bulk credential onboarding: upload any CSV/XLSX → AI normalizes →
 * review (fix rows needing input) → confirm → credentials set + collection triggered.
 */
export const CollectorOnboardDialog = ({ open, onOpenChange, accountId, suppliers = [], onApplied }) => {
  const [step, setStep] = useState('upload'); // upload | processing | review | applying | done
  const [draft, setDraft] = useState(null);
  const [lineIdx, setLineIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [rowFixes, setRowFixes] = useState({}); // row_id -> {supplier_short, supplier_name, username, password}
  const fileRef = useRef(null);

  useEffect(() => {
    if (step !== 'processing' && step !== 'applying') return;
    const t = setInterval(() => setLineIdx((i) => (i + 1) % PROCESSING_LINES.length), 1200);
    return () => clearInterval(t);
  }, [step]);

  const reset = () => { setStep('upload'); setDraft(null); setResult(null); setRowFixes({}); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStep('processing'); setLineIdx(0);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/portal-collectors/onboard/normalize', fd, {
        params: { account_id: accountId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDraft(data);
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read that file');
      setStep('upload');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveRow = async (row) => {
    const fix = rowFixes[row.row_id] || {};
    try {
      const { data } = await api.patch(
        `/portal-collectors/onboard/drafts/${draft.draft_id}/rows/${row.row_id}`,
        fix, { params: { account_id: accountId } },
      );
      setDraft(data);
      toast.success('Row updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update row');
    }
  };

  const apply = async () => {
    setStep('applying'); setLineIdx(0);
    try {
      const { data } = await api.post(
        `/portal-collectors/onboard/drafts/${draft.draft_id}/apply`, {},
        { params: { account_id: accountId } },
      );
      setResult(data);
      setStep('done');
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not onboard');
      setStep('review');
    }
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 300); };

  const ready = draft?.summary?.ready || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="rounded-none max-w-3xl" data-testid="collector-onboard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-600" /> AI Onboard Collectors
          </DialogTitle>
          <DialogDescription>
            Upload a CSV/XLSX of portal logins in any format — I'll convert it to our credential
            format, match each to a collector, then set them up and start collecting bills.
          </DialogDescription>
        </DialogHeader>

        {/* UPLOAD */}
        {step === 'upload' && (
          <div className="py-8 text-center">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={handleFile} data-testid="onboard-file-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mx-auto flex w-full max-w-md flex-col items-center gap-3 border-2 border-dashed border-slate-300 p-10 hover:border-blue-500 transition-colors"
              data-testid="onboard-upload-zone"
            >
              <Upload className="h-8 w-8 text-slate-400" />
              <span className="text-sm font-medium">Click to upload credentials CSV / XLSX</span>
              <span className="text-xs text-muted-foreground">Any column layout — vendor, account #, username, password</span>
            </button>
          </div>
        )}

        {/* PROCESSING / APPLYING (shimmer) */}
        {(step === 'processing' || step === 'applying') && (
          <div className="py-12 text-center space-y-4">
            <div className="relative mx-auto h-12 w-12">
              <Sparkles className="h-12 w-12 text-blue-500 animate-pulse" />
            </div>
            <div className="text-sm font-medium text-slate-700">{PROCESSING_LINES[lineIdx]}</div>
            <div className="mx-auto h-1.5 w-64 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-1/3 animate-[shimmer_1.2s_infinite] bg-blue-500" style={{ animation: 'onbShimmer 1.4s ease-in-out infinite' }} />
            </div>
            <style>{`@keyframes onbShimmer{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>
          </div>
        )}

        {/* REVIEW */}
        {step === 'review' && draft && (
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            <div className="flex gap-2 text-sm">
              <Badge variant="outline" className="rounded-none border-emerald-500 text-emerald-700">{ready} ready</Badge>
              {draft.summary.needs_input > 0 && (
                <Badge variant="outline" className="rounded-none border-amber-500 text-amber-700">{draft.summary.needs_input} need input</Badge>
              )}
              <span className="text-xs text-muted-foreground self-center">from {draft.source_filename} ({draft.source === 'ai' ? 'AI-normalized' : 'auto-mapped'})</span>
            </div>
            <table className="w-full text-sm border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 text-xs uppercase">Account</th>
                  <th className="text-left p-2 text-xs uppercase">Supplier</th>
                  <th className="text-left p-2 text-xs uppercase">Username</th>
                  <th className="text-left p-2 text-xs uppercase">Password</th>
                  <th className="text-left p-2 text-xs uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {draft.rows.map((row) => {
                  const needs = row.needs_input || [];
                  const fix = rowFixes[row.row_id] || {};
                  const setFix = (patch) => setRowFixes((p) => ({ ...p, [row.row_id]: { ...p[row.row_id], ...patch } }));
                  return (
                    <tr key={row.row_id} className="border-t align-top" data-testid={`onboard-row-${row.row_id}`}>
                      <td className="p-2">
                        <div className="font-medium">{row.account_name || '—'}</div>
                        <div className="text-xs text-muted-foreground font-mono">{row.account_number || '—'}</div>
                      </td>
                      <td className="p-2 min-w-[180px]">
                        {needs.includes('no_supplier_match') ? (
                          <SupplierCombobox
                            suppliers={suppliers}
                            value={fix.supplier_short}
                            onChange={(s, n) => setFix({ supplier_short: s, supplier_name: n })}
                            testId={`onboard-supplier-${row.row_id}`}
                          />
                        ) : (
                          <Badge variant="outline" className="rounded-none text-xs">{row.supplier_name}</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        {needs.includes('missing_username') ? (
                          <Input className="rounded-none h-8 text-xs" placeholder="username"
                            value={fix.username || ''} onChange={(e) => setFix({ username: e.target.value })}
                            data-testid={`onboard-username-${row.row_id}`} />
                        ) : (<span className="font-mono text-xs">{row.username}</span>)}
                      </td>
                      <td className="p-2">
                        {needs.includes('missing_password') ? (
                          <Input type="password" className="rounded-none h-8 text-xs" placeholder="password"
                            value={fix.password || ''} onChange={(e) => setFix({ password: e.target.value })}
                            data-testid={`onboard-password-${row.row_id}`} />
                        ) : (<span className="font-mono text-xs">••••{row.password_last4 || ''}</span>)}
                      </td>
                      <td className="p-2">
                        {row.status === 'ready' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="h-4 w-4" /> Ready</span>
                        ) : (
                          <div className="space-y-1">
                            <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                              <AlertTriangle className="h-4 w-4" /> Needs {needs.map((n) => ISSUE_LABEL[n] || n).join(', ')}
                            </span>
                            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs w-full"
                              onClick={() => saveRow(row)} data-testid={`onboard-fix-${row.row_id}`}>Save fix</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* DONE */}
        {step === 'done' && result && (
          <div className="py-8 text-center space-y-3" data-testid="onboard-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <div className="text-base font-semibold">{result.created} credential(s) set & triggered</div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Verifying the credentials can take a few minutes. Come back here to watch the status
              lights turn green, or ask the AI agent for a status report.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 'review' && (
            <>
              <Button variant="outline" className="rounded-none" onClick={close}>Cancel</Button>
              <Button className="rounded-none" disabled={ready === 0} onClick={apply} data-testid="onboard-confirm-btn">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Confirm &amp; Onboard {ready}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button className="rounded-none" onClick={close} data-testid="onboard-close-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CollectorOnboardDialog;
